import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { PROJECTED_CASH_FLOW_OPTION_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { PERIODS_OPERATION_TYPES, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { calculateNetCashNetCashInTheBeginningAndEnd } from './helper';
import { observedFields } from '../../utils/form';

export const useGetTotalDirectNetCashCalculation = () => {
  const { setValue } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [netCashIncreaseField, netCashInTheBeginningField, netCashInTheEndField, cashEquivalentField] =
    observedFields.totalDirectNetCash.setCalculatedValueFieldNames;

  const getTotalNetCashCalculationHandler = useMemo(
    () =>
      debounce((content) => {
        if (!canAccess) return;
        const [
          operatingActivitiesNetCashDirect,
          investingActivitiesNetCash,
          financialActivitiesNetCash,
          initialCash,
          netCashProvidedByIndirectOperatingActivities,
          selectedType,
        ] = content;

        const operatingActivitiesNetCash =
          selectedType === PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT
            ? operatingActivitiesNetCashDirect
            : netCashProvidedByIndirectOperatingActivities;

        const netCashIncrease = summarizeSubtractPeriodValues(
          [operatingActivitiesNetCash, investingActivitiesNetCash, financialActivitiesNetCash],
          PERIODS_OPERATION_TYPES.ADD,
        );

        const { netCashInTheEnd, netCashInTheBeginning } = calculateNetCashNetCashInTheBeginningAndEnd(
          netCashIncrease,
          initialCash,
        );

        setValue(netCashIncreaseField, netCashIncrease);
        setValue(netCashInTheBeginningField, netCashInTheBeginning);
        setValue(netCashInTheEndField, netCashInTheEnd);
        setValue(cashEquivalentField, netCashInTheEnd);
      }, 500),
    [],
  );

  return getTotalNetCashCalculationHandler;
};
